import { useCallback, useEffect, useState } from "react";
import {
  buildMedicalBillRecordsForStorage,
  getDefaultMedicalProviderName,
  getMedicalBillRecordsByClient,
  getMedicalBillRecordsUpdatePath,
  getProviderNameForBillRecord
} from "@demandproai/shared";
import useDemandStudioEditActions from "./useDemandStudioEditActions";

const MEDICAL_BILL_DEFAULT_ROW = {
  totalBillAmount: "",
  date: "",
  medicalProviderName: "",
};

const useDemandStudioMedicalBillEdit = ({ caseId, caseDetails, sectionDefinitions, resultRequests, onEditPersisted }) => {
  const [medicalBillRecordsByClientId, setMedicalBillRecordsByClientId] = useState({});
  const {sendEditAction, isSavingManualEdit} = useDemandStudioEditActions({caseId, onEditPersisted});

  useEffect(() => {
    if (!resultRequests?.length || !sectionDefinitions.length) return;
    setMedicalBillRecordsByClientId(
      getMedicalBillRecordsByClient({ resultRequests, sectionDefinitions })
    );
  }, [sectionDefinitions, resultRequests]);

  const persistMedicalBillRecords = useCallback(
    ({ clientId, rows }) => {
      const fieldPath = getMedicalBillRecordsUpdatePath(clientId);
      if (!fieldPath) return;
      const rowsForStorage = buildMedicalBillRecordsForStorage(rows);
      sendEditAction({[fieldPath]: rowsForStorage});
    },
    [sendEditAction]
  );

  const updateMedicalBillRecordField = useCallback(
    ({ clientId, rowIndex, field, value }) => {
      if (!clientId || !field) return;
      setMedicalBillRecordsByClientId((prev) => {
        const existingRows = Array.isArray(prev[clientId]) ? prev[clientId] : [];
        if (rowIndex < 0 || rowIndex >= existingRows.length) {
          return prev;
        }
        const nextRows = existingRows.map((row, index) => {
          if (index !== rowIndex) return row;
          const nextProviderName = getProviderNameForBillRecord(row);
          return {
            ...row,
            medicalProviderName: nextProviderName,
            [field]: value,
          };
        });
        persistMedicalBillRecords({ clientId, rows: nextRows });
        return {
          ...prev,
          [clientId]: nextRows,
        };
      });
    },
    [persistMedicalBillRecords]
  );

  const addMedicalBillRecordRow = useCallback(
    ({ clientId }) => {
      if (!clientId) return;
      setMedicalBillRecordsByClientId((prev) => {
        const existingRows = Array.isArray(prev[clientId]) ? prev[clientId] : [];
        const defaultMedicalProviderName = getDefaultMedicalProviderName({
          caseDetails,
          clientId,
          existingRows,
        });
        const nextRows = [
          ...existingRows,
          {
            ...MEDICAL_BILL_DEFAULT_ROW,
            medicalProviderName: defaultMedicalProviderName,
          },
        ];
        persistMedicalBillRecords({ clientId, rows: nextRows });
        return {
          ...prev,
          [clientId]: nextRows,
        };
      });
    },
    [caseDetails, persistMedicalBillRecords]
  );

  const removeMedicalBillRecordRow = useCallback(
    ({ clientId, rowIndex }) => {
      if (!clientId) return;
      setMedicalBillRecordsByClientId((prev) => {
        const existingRows = Array.isArray(prev[clientId]) ? prev[clientId] : [];
        if (rowIndex < 0 || rowIndex >= existingRows.length) {
          return prev;
        }
        const nextRows = existingRows.filter((_, index) => index !== rowIndex);
        persistMedicalBillRecords({ clientId, rows: nextRows });
        return {
          ...prev,
          [clientId]: nextRows,
        };
      });
    },
    [persistMedicalBillRecords]
  );

  return {
    medicalBillRecordsByClientId,
    updateMedicalBillRecordField,
    addMedicalBillRecordRow,
    removeMedicalBillRecordRow,
    isSavingManualEdit,
  };
};

export default useDemandStudioMedicalBillEdit;
